import React, { useState, useEffect } from "react";
import { DateDisplay } from "../DateDisplay";
import {
	FeatureToggle,
	FeatureToggleEditRequest,
	FeatureToggleEditRequestInitialValue,
	FeatureToggleEditRequestValidationSchema,
	DeploymentRing,
} from "../../models/FeatureToggles";
import { ValueDisplayProps } from "../ValueDisplay";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import { FormikTextInputField } from "../Form/FormikTextInput";
import { FormikCheckboxInputField } from "../Form/FormikCheckboxInput";
import { showSnack } from "../../utils/component";
import { InlineValueDisplayList } from "../InlineValueDisplayList";
import {
	getDeploymentRings,
	deleteFeatureToggle,
	addFeatureToggle,
	editFeatureToggle,
} from "../../services/api/featureToggles.api";
import { getProductTiers, ProductTier } from "../../services/api/productTiers.api";
import { Button } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { tryGetProp } from "../../utils";

interface FeatureToggleEditProps {
	// optional feature toggle object
	// if not provided, form will be displayed in add mode
	featureToggle?: FeatureToggle | null;
	// product id
	productId: string;
	// function to call on successful save
	onSuccess?: () => void;
}

const getAuditDataDisplay = (data: FeatureToggle) => {
	const detailsToDisplay: ValueDisplayProps[] = [
		{
			label: "Created on",
			node: <DateDisplay date={data.created} />,
		},
		{
			label: "Created by",
			node: data.createdBy,
		},
		{
			label: "Updated on",
			node: <DateDisplay date={data.updated} />,
		},
		{
			label: "Updated by",
			node: data.lastUpdatedBy,
		},
	];

	return <InlineValueDisplayList displayItems={detailsToDisplay}></InlineValueDisplayList>;
};

/**
 * Display a feature toggle
 * @param props
 */
export const FeatureToggleEdit = (props: FeatureToggleEditProps) => {
	const history = useHistory();
	const { enqueueSnackbar } = useSnackbar();
	const [deploymentRings, setDeploymentRings] = useState<DeploymentRing[]>([]);
	const [productTiers, setProductTiers] = useState<ProductTier[]>([]);
	const [isFormSubmitting, setIsFormSubmitting] = useState(false);
	const [loadedLookupData, setLoadedLookupData] = useState(false);

	const isEditMode = props.featureToggle != null;

	const initialValues: FeatureToggleEditRequest = {
		featureToggleId: tryGetProp(
			FeatureToggleEditRequestInitialValue.featureToggleId!,
			() => props.featureToggle!.featureToggleId,
		),
		name: tryGetProp(FeatureToggleEditRequestInitialValue.name, () => props.featureToggle!.name),
		displayName: tryGetProp(FeatureToggleEditRequestInitialValue.displayName, () => props.featureToggle!.displayName),
		deploymentRingId: tryGetProp(
			FeatureToggleEditRequestInitialValue.deploymentRingId,
			() => props.featureToggle!.deploymentRingId,
		),
		productTierIds: tryGetProp(FeatureToggleEditRequestInitialValue.productTierIds, () =>
			props.featureToggle!.productTiers.map((pt) => pt.id),
		),
	};

	useEffect(() => {
		const getData = async () => {
			const deploymentRingPromise = getDeploymentRings(props.productId)();
			const productTierPromise = getProductTiers(props.productId)();

			const deploymentRingResponse = await deploymentRingPromise;
			const productTierResponse = await productTierPromise;

			setDeploymentRings(tryGetProp([], () => deploymentRingResponse.data));
			setProductTiers(tryGetProp([], () => productTierResponse.data));

			setLoadedLookupData(true);
		};

		getData();
	}, [props.productId]);

	const handleDelete = () => {
		deleteFeatureToggle(props.productId, props.featureToggle!.featureToggleId)()
			.then(() => {
				history.push(`/Products/${props.productId}/FeatureToggles`);
			})
			.catch((error) => {
				console.log({ error });
				showSnack(enqueueSnackbar, "There was an error while deleting the feature toggle", "error");
			});
	};

	const submitForm = async (values, { resetForm }) => {
		try {
			setIsFormSubmitting(true);

			if (isEditMode) {
				await editFeatureToggle(props.productId, props.featureToggle!.featureToggleId, values)();
			} else {
				await addFeatureToggle(props.productId, values)();
				resetForm();
			}

			showSnack(enqueueSnackbar, "Changes saved successfully");

			if (props.onSuccess) {
				props.onSuccess();
			}
		} catch (error: any) {
			console.log({ error });

			if (tryGetProp(0, () => error.response.status) === 409) {
				showSnack(enqueueSnackbar, "Validation error, please make sure the name is unique", "error");
			} else {
				showSnack(enqueueSnackbar, "There was an error while saving changes", "error");
			}
		}

		setIsFormSubmitting(false);
	};

	return (
		<div>
			{isEditMode && getAuditDataDisplay(props.featureToggle!)}

			{loadedLookupData && (
				<Formik
					initialValues={initialValues}
					validationSchema={FeatureToggleEditRequestValidationSchema}
					onSubmit={submitForm}
				>
					{({ handleSubmit }) => {
						return (
							<div style={{ marginTop: 30 }}>
								<FormikTextInputField name="name" label="Name" optimized={true} />

								<FormikTextInputField name="displayName" label="Display name" optimized={true} />

								<FormikTextInputField
									name="deploymentRingId"
									label="Deployment ring"
									optimized={false}
									isSelect={true}
									selectOptions={deploymentRings.map((x) => ({
										val: x.id,
										text: x.name,
									}))}
									appendEmptySelectOption={true}
								/>

								<FormikCheckboxInputField
									name="productTierIds"
									label="Product tiers"
									optimized={true}
									selectOptions={productTiers.map((x) => ({
										val: x.productTierId,
										text: x.productTierName,
									}))}
								/>

								<div style={{ marginTop: 20 }}>
									<Button
										data-testid="edit-feature-toggle-save-btn"
										variant="contained"
										disabled={isFormSubmitting}
										color="primary"
										style={{ marginRight: 12 }}
										onClick={() => handleSubmit()}
									>
										{isEditMode ? "Save feature toggle" : "Add feature toggle"}
									</Button>
									{isEditMode && (
										<Button
											data-testid="edit-feature-toggle-delete-btn"
											variant="outlined"
											disabled={isFormSubmitting}
											color="secondary"
											onClick={() => {
												if (confirm("Are you sure you want to delete this feature toggle?")) {
													handleDelete();
												}
											}}
										>
											Delete feature toggle
										</Button>
									)}
								</div>
							</div>
						);
					}}
				</Formik>
			)}
		</div>
	);
};
